import { useNotifyAlert } from 'context/NotifyAlert';
import { useCallback, useRef, useState } from 'react';

export const BAR_MULTIPLIER = 25;
export const MAX_BAR_HEIGHT = 45;

const useAudioHandler = () => {
	const { setShowNotify } = useNotifyAlert();

	const [ isRecording, setIsRecording ] = useState( false );
	const [ recordingTime, setRecordingTime ] = useState( 0 );
	const [ audioLevels, setAudioLevels ] = useState( [] );

	const mediaRecorderRef = useRef( null );
	const recordingTimerRef = useRef( null );
	const analyserRef = useRef( null );
	const dataArrayRef = useRef( null );
	const animationRef = useRef( null );
	const streamRef = useRef( null );
	const audioContextRef = useRef( null );
	const chunksRef = useRef( [] );
	const isPlayingRef = useRef( false );

	const analyzeAudio = useCallback( () => {
		if ( !analyserRef.current || !dataArrayRef.current ) {
			return;
		}

		const analyser = analyserRef.current;
		const dataArray = dataArrayRef.current;

		// Usar getByteTimeDomainData para capturar amplitude real da onda sonora
		analyser.getByteTimeDomainData( dataArray );

		// Calcular RMS (Root Mean Square) para intensidade real
		let sumSquares = 0;
		let peak = 0;

		for ( let i = 0; i < dataArray.length; i++ ) {
			// Converter de 0-255 para -1 a 1
			const sample = ( dataArray[i] - 128 ) / 128;
			sumSquares += sample * sample;

			// Capturar pico de amplitude
			const amplitude = Math.abs( sample );
			if ( amplitude > peak ) {
				peak = amplitude;
			}
		}

		// Calcular RMS
		const rms = Math.sqrt( sumSquares / dataArray.length );

		// Usar uma combinação de RMS e pico para responsividade
		const intensity = rms * 0.7 + peak * 0.3;

		// Aplicar boost e normalizar
		const boostedLevel = Math.min( intensity * 15, 1 );

		// Adicionar o nível atual ao array
		setAudioLevels( ( prev ) => {
			const newLevels = [ ...prev, boostedLevel ];
			const trimmed = newLevels.slice( -50 );
			return trimmed;
		} );
	}, [] );

	const startAnalysisLoop = useCallback( () => {
		const loop = () => {
			if ( isPlayingRef.current ) {
				analyzeAudio();
				animationRef.current = requestAnimationFrame( loop );
			}
		};
		loop();
	}, [ isRecording, analyzeAudio ] ); //eslint-disable-line

	const startRecording = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia( { audio: true } );

			const mediaRecorder = new MediaRecorder( stream, {
				mimeType: 'audio/webm;codecs=opus',
			} );

			const audioChunks = [];

			// Web Audio API
			const audioContext = new ( window.AudioContext || window.webkitAudioContext )();
			streamRef.current = stream;
			if ( audioContext.state === 'suspended' ) {
				await audioContext.resume();
			}

			const source = audioContext.createMediaStreamSource( stream );
			const analyser = audioContext.createAnalyser();

			analyser.fftSize = 2048; // Maior resolução para melhor captura
			analyser.smoothingTimeConstant = 0.1; // Menos suavização para mais responsividade
			analyser.minDecibels = -100;
			analyser.maxDecibels = -30;

			const bufferLength = analyser.frequencyBinCount;
			const dataArray = new Uint8Array( bufferLength );

			source.connect( analyser );

			audioContextRef.current = audioContext;
			analyserRef.current = analyser;
			dataArrayRef.current = dataArray;

			mediaRecorderRef.current = mediaRecorder;
			chunksRef.current = [];

			mediaRecorder.ondataavailable = ( event ) => {
				if ( event.data.size > 0 ) {
					audioChunks.push( event.data );
					chunksRef.current.push( event.data );
				}
			};

			mediaRecorder.onstop = () => {
				if ( audioContext ) {
					audioContext.close();
				}
				cancelAnimationFrame( animationRef.current );
			};

			mediaRecorderRef.current = {
				recorder : mediaRecorder,
				chunks   : audioChunks,
			};

			mediaRecorder.start( 100 );
			setIsRecording( true );
			setRecordingTime( 0 );
			setAudioLevels( [] );
			isPlayingRef.current = true;

			recordingTimerRef.current = setInterval( () => {
				setRecordingTime( ( prev ) => prev + 1 );
			}, 1000 );

			setTimeout( () => {
				startAnalysisLoop();
			}, 500 );
		} catch ( error ) {
			console.error( 'Erro ao iniciar gravação:', error );
			setShowNotify( {
				status : 'error',
				text   : 'Erro ao acessar o microfone. Verifique as permissões do navegador.',
			} );
		}
	};

	const cancelRecording = () => {
		if ( mediaRecorderRef.current && isRecording ) {
			const { recorder } = mediaRecorderRef.current;
			recorder.stream.getTracks().forEach( ( track ) => track.stop() );
			clearInterval( recordingTimerRef.current );
			setIsRecording( false );
			setRecordingTime( 0 );
			isPlayingRef.current = false;
		}
	};

	const formatRecordingTime = ( seconds ) => {
		const minutes = Math.floor( seconds / 60 );
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart( 2, '0' )}`;
	};

	return {
		isRecording,
		recordingTime,
		audioLevels,
		startRecording,
		recordingTimerRef,
		mediaRecorderRef,
		cancelRecording,
		formatRecordingTime,
		setIsRecording,
		setRecordingTime,
	};

};

export default useAudioHandler;
